import React, {FC, useEffect, useRef, useState} from 'react';
import styles from './RulerOverlay.module.css';
import RulerConfirmWidthDialog from "./RulerConfirmWidthDialog";
import {getAppConfig} from '../../../auth/GetAppConfig';

type Phase = 'choose-left' | 'choose-right' | 'confirm-width' | 'active';
type CalibrationDrag = 'left' | 'right' | 'line' | null;
type ActiveDrag = 'active-left' | 'active-right' | 'active-line' | 'vertical-top' | null;
type RailSide = 'left' | 'right';
type HoverTarget = 'left-rail' | 'right-rail' | 'left-arrow' | 'right-arrow' | 'active-line' | 'vertical' | 'vertical-top' | null;

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const distPct = (a: number, b: number) => Math.abs(a - b);
const fallbackRailWidthMeters = 1.6;
const activeHorizontalStartDistance = 1.0;
const activeVerticalStartDistance = 2.1;
const verticalTopGrabPixels = 20;
const verticalTopCircleRadiusPx = 15;
const railWidthStorageKey = 'ruler:railWidthMeters';

const formatDistance = (value: number) => value.toFixed(2);
const toggleRailSide = (side: RailSide): RailSide => side === 'left' ? 'right' : 'left';
const parseRailWidthMeters = (value: unknown): number | null => {
    const parsedValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const getDefaultRailWidthMeters = () => {
    try {
        const configuredWidthMeters = parseRailWidthMeters((getAppConfig() as {defaultRailWidthMeters?: unknown}).defaultRailWidthMeters);
        return configuredWidthMeters ?? fallbackRailWidthMeters;
    } catch (e) {
        return fallbackRailWidthMeters;
    }
};

const getStoredRailWidthMeters = () => {
    try {
        return parseRailWidthMeters(localStorage.getItem(railWidthStorageKey));
    } catch (e) {
        return null;
    }
};

const persistRailWidthMeters = (railWidthMeters: number, defaultRailWidthMeters: number) => {
    try {
        if (Math.abs(railWidthMeters - defaultRailWidthMeters) < 0.0001) {
            localStorage.removeItem(railWidthStorageKey);
            return;
        }

        localStorage.setItem(railWidthStorageKey, String(railWidthMeters));
    } catch (e) {
        // ignore
    }
};

const RulerOverlay: FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const defaultRailWidthMeters = getDefaultRailWidthMeters();

    const [phase, setPhase] = useState<Phase>('choose-left');
    const [dragging, setDragging] = useState<CalibrationDrag>(null);
    const [draggingActive, setDraggingActive] = useState<ActiveDrag>(null);
    const [measurementSide, setMeasurementSide] = useState<RailSide>('left');
    const [hoverTarget, setHoverTarget] = useState<HoverTarget>(null);
    const [verticalLineCursor, setVerticalLineCursor] = useState<'ew-resize' | 'ns-resize'>('ew-resize');

    const [lineHoverCanGrab, setLineHoverCanGrab] = useState(false);

    // Positions are stored in percent (0..100) so they work with responsive sizing.
    const [leftX, setLeftX] = useState(40);
    const [rightX, setRightX] = useState(60);
    const [lineY, setLineY] = useState(80);
    const [railWidthMeters, setRailWidthMeters] = useState<number>(() => getStoredRailWidthMeters() ?? defaultRailWidthMeters);

    const [activeLeftX, setActiveLeftX] = useState<number | null>(null);
    const [activeRightX, setActiveRightX] = useState<number | null>(null);
    const [verticalTopY, setVerticalTopY] = useState<number | null>(null);

    useEffect(() => {
        const handleMove = (e: PointerEvent) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const xPct = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
            const yPct = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);

            if (draggingActive) {
                if (draggingActive === 'active-left') {
                    setActiveLeftX(xPct);
                } else if (draggingActive === 'active-right') {
                    setActiveRightX(xPct);
                } else if (draggingActive === 'active-line') {
                    setLineY(yPct);
                } else if (draggingActive === 'vertical-top') {
                    if (measurementSide === 'left') {
                        setActiveLeftX(xPct);
                    } else {
                        setActiveRightX(xPct);
                    }
                    setVerticalTopY(yPct);
                }
                return;
            }

            if (dragging === 'left') {
                const nextLeftX = clamp(xPct, 0, rightX - 0.1);
                const deltaX = nextLeftX - leftX;
                setLeftX(nextLeftX);

                if (phase === 'active' && activeLeftX != null) {
                    setActiveLeftX(clamp(activeLeftX + deltaX, 0, 100));
                }
            } else if (dragging === 'right') {
                const nextRightX = clamp(xPct, leftX + 0.1, 100);
                const deltaX = nextRightX - rightX;
                setRightX(nextRightX);

                if (phase === 'active' && activeRightX != null) {
                    setActiveRightX(clamp(activeRightX + deltaX, 0, 100));
                }
            } else if (dragging === 'line') {
                setLineY(yPct);
            }
        };

        const handleUp = () => {
            if (draggingActive) {
                setDraggingActive(null);
                return;
            }

            if (!dragging) return;
            if (phase === 'active') {
                setDragging(null);
                return;
            }
            if (dragging === 'line') {
                setPhase('choose-left');
            } else if (dragging === 'left') {
                setPhase('choose-right');
            } else {
                setPhase('confirm-width');
            }
            setDragging(null);
        };

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
        };
    }, [activeLeftX, activeRightX, dragging, draggingActive, leftX, measurementSide, phase, rightX]);

    const showRightMarker = true;

    const markerNoGrabRadiusPct = 4; // prevents "grabbing line" when near a marker
    const canGrabLine = (xPct: number, yPct: number) => {
        const nearLine = Math.abs(yPct - lineY) < 2.2;
        if (!nearLine) return false;

        const nearLeft = distPct(xPct, leftX) < markerNoGrabRadiusPct;
        const nearRight = showRightMarker && distPct(xPct, rightX) < markerNoGrabRadiusPct;
        return !nearLeft && !nearRight;
    };

    const initialiseActiveRulers = (widthMeters: number) => {
        const metersPerPercent = widthMeters / Math.max(0.0001, Math.abs(rightX - leftX));
        const outwardOffsetPct = activeHorizontalStartDistance / metersPerPercent;
        const verticalOffsetPct = activeVerticalStartDistance / metersPerPercent;
        setMeasurementSide('left');
        setActiveLeftX(clamp(leftX - outwardOffsetPct, 0, 100));
        setActiveRightX(clamp(rightX + outwardOffsetPct, 0, 100));
        setVerticalTopY(clamp(lineY - verticalOffsetPct, 0, 100));
        setPhase('active');
    };

    // Compute real pixel distances and derive meters-per-pixel from the calibration
    const overlayRect = containerRef.current?.getBoundingClientRect();

    const percentBetweenCalMarkers = Math.max(0.0001, Math.abs(rightX - leftX));
    const leftRailX = leftX;
    const rightRailX = rightX;

    const activeLeft = activeLeftX ?? leftRailX;
    const activeRight = activeRightX ?? rightRailX;
    const activeLineLeft = Math.min(activeLeft, activeRight);
    const activeLineWidth = Math.max(0.1, Math.abs(activeRight - activeLeft));
    const activeLineMid = (activeLeft + activeRight) / 2;
    const selectedRailX = measurementSide === 'left' ? leftRailX : rightRailX;
    const rulerCenterX = (leftRailX + rightRailX) / 2;

    let __rulerComputed: {
        activeLeftDistanceMeters: number;
        activeRightDistanceMeters: number;
        verticalHeightMeters: number;
        verticalAnchorX: number;
        verticalMidY: number;
    };

    if (overlayRect) {
        const pixelDistanceX = (percentBetweenCalMarkers / 100) * overlayRect.width;
        const metersPerPixel = railWidthMeters / Math.max(0.0001, pixelDistanceX);

        const activeLeftPixels = (Math.abs(activeLeft - selectedRailX) / 100) * overlayRect.width;
        const activeRightPixels = (Math.abs(activeRight - selectedRailX) / 100) * overlayRect.width;

        const activeLeftDistanceMeters = activeLeftPixels * metersPerPixel;
        const activeRightDistanceMeters = activeRightPixels * metersPerPixel;

        const verticalAnchorX = measurementSide === 'left' ? activeLeft : activeRight;
        const verticalMidY = verticalTopY == null ? lineY : (verticalTopY + lineY) / 2;

        const verticalPixels = verticalTopY == null ? 0 : (Math.abs(lineY - verticalTopY) / 100) * overlayRect.height;
        // Enforce 1:1: use horizontal metersPerPixel for vertical distances too
        const verticalHeightMeters = verticalPixels * metersPerPixel;

        // expose computed values to the render below by shadowing consts
        // (we'll return a block with these values used inline)
        // To keep code structure minimal, assign to outer-scope via object
        __rulerComputed = {
            activeLeftDistanceMeters,
            activeRightDistanceMeters,
            verticalHeightMeters,
            verticalAnchorX,
            verticalMidY
        };
    } else {
        // fallback to percent-based math when rect unavailable
        const metersPerPercentX = railWidthMeters / percentBetweenCalMarkers;
        const activeLeftDistanceMeters = Math.abs(activeLeft - selectedRailX) * metersPerPercentX;
        const activeRightDistanceMeters = Math.abs(activeRight - selectedRailX) * metersPerPercentX;
        const metersPerPercentY = metersPerPercentX;
        const verticalHeightMeters = verticalTopY == null ? 0 : Math.abs((lineY - verticalTopY) * metersPerPercentY);
        const verticalAnchorX = measurementSide === 'left' ? activeLeft : activeRight;
        const verticalMidY = verticalTopY == null ? lineY : (verticalTopY + lineY) / 2;

        __rulerComputed = {
            activeLeftDistanceMeters,
            activeRightDistanceMeters,
            verticalHeightMeters,
            verticalAnchorX,
            verticalMidY
        };
    }

    const getHintText = () => {
        if (phase === 'choose-left') return 'Drag the left marker to the inside of the left rail';
        if (phase === 'choose-right') return 'Now drag the right marker to the inside of the right rail';
        if (phase === 'confirm-width') return `Confirm the rail width (default ${fallbackRailWidthMeters * 1000})`;

        switch (hoverTarget) {
            case 'left-rail':
                return 'Drag to adjust, right-click to switch alignment';
            case 'right-rail':
                return 'Drag to adjust, right-click to switch alignment';
            case 'left-arrow':
            case 'right-arrow':
                return 'Drag to adjust, right-click to switch alignment';
            case 'active-line':
                return 'Drag up or down to adjust';
            case 'vertical':
                return 'Drag left or right to move the vertical measure';
            case 'vertical-top':
                return 'Drag to adjust';
            default:
                return `${measurementSide} rail aligned`;
        }
    };

    const getVerticalDragMode = (clientY: number, target: HTMLDivElement): 'horizontal' | 'vertical' => {
        const rect = target.getBoundingClientRect();
        return clientY - rect.top <= verticalTopGrabPixels ? 'vertical' : 'horizontal';
    };

    const dialogPosition = overlayRect
        ? {
            left: overlayRect.left + (overlayRect.width / 2),
            top: overlayRect.top + ((overlayRect.height * lineY) / 100)
        }
        : undefined;

    return (
        <div
            className={styles.RulerOverlay}
            ref={containerRef}
            data-id="ruler-overlay"
            onContextMenu={(e) => e.preventDefault()}
        >
            <RulerConfirmWidthDialog
                isOpen={phase === 'confirm-width'}
                defaultWidthMeters={fallbackRailWidthMeters}
                dialogPosition={dialogPosition}
                onAdjust={() => {
                    setPhase('choose-left');
                    setRailWidthMeters(defaultRailWidthMeters);
                    persistRailWidthMeters(defaultRailWidthMeters, defaultRailWidthMeters);
                }}
                onConfirm={(widthMeters) => {
                    const nextRailWidthMeters = widthMeters || fallbackRailWidthMeters;
                    setRailWidthMeters(nextRailWidthMeters);
                    persistRailWidthMeters(nextRailWidthMeters, defaultRailWidthMeters);
                    initialiseActiveRulers(nextRailWidthMeters);
                }}
            />

            <div
                className={styles.GreyLineHandle}
                data-id="ruler-grey-line"
                onPointerDown={(e) => {
                    if (!containerRef.current) return;
                    const rect = containerRef.current.getBoundingClientRect();
                    const xPct = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
                    const yPct = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
                    if (!canGrabLine(xPct, yPct)) return;

                    e.preventDefault();
                    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                    setDragging('line');
                }}
                onPointerMove={(e) => {
                    if (!containerRef.current) return;
                    const rect = containerRef.current.getBoundingClientRect();
                    const xPct = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
                    const yPct = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);
                    setLineHoverCanGrab(canGrabLine(xPct, yPct));
                }}
                onPointerLeave={() => setLineHoverCanGrab(false)}
                style={{top: `${lineY}%`, cursor: dragging === 'line' ? 'ns-resize' : lineHoverCanGrab ? 'ns-resize' : 'default'}}
            >
                <div className={styles.GreyLine} />
                    <div
                        className={`${styles.GrabIcon} ${styles.GrabIconRed} ${(dragging === 'line' || draggingActive === 'active-line') ? styles.Grabbing : ''}`}
                        aria-hidden
                        style={{ left: `${rulerCenterX}%` }}
                    />
            </div>

            <div
                className={styles.MarkerHandle}
                data-id="ruler-left-marker"
                style={{left: `${leftX}%`, top: `${lineY}%`}}
                onPointerDown={(e) => {
                    if (phase === 'active' && e.button === 2) {
                        e.preventDefault();
                        setMeasurementSide(toggleRailSide);
                        return;
                    }
                    if (e.button !== 0) return;
                    e.preventDefault();
                    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                    setDragging('left');
                }}
                onMouseEnter={() => setHoverTarget('left-rail')}
                onMouseLeave={() => setHoverTarget(null)}
                title={phase === 'active' ? 'Drag to adjust, right-click to switch alignment' : 'Drag to inside of left rail'}
            >
                <div className={`${styles.MarkerVisual} ${measurementSide === 'left' ? styles.MarkerYellow : styles.MarkerRed}`} />
            </div>

            {showRightMarker && (
                <div
                    className={styles.MarkerHandle}
                    data-id="ruler-right-marker"
                    style={{left: `${rightX}%`, top: `${lineY}%`}}
                    onPointerDown={(e) => {
                        if (phase === 'active' && e.button === 2) {
                            e.preventDefault();
                            setMeasurementSide(toggleRailSide);
                            return;
                        }
                        if (e.button !== 0) return;
                        e.preventDefault();
                        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                        setDragging('right');
                    }}
                    onMouseEnter={() => setHoverTarget('right-rail')}
                    onMouseLeave={() => setHoverTarget(null)}
                    title={phase === 'active' ? 'Drag to adjust, right-click to switch alignment' : 'Drag to inside of right rail'}
                >
                    <div className={`${styles.MarkerVisual} ${measurementSide === 'right' ? styles.MarkerYellow : styles.MarkerRed}`} />
                </div>
            )}

            {phase === 'active' && (
                <>
                    <div
                        className={styles.ActiveLine}
                        style={{top: `${lineY}%`, left: `${activeLineLeft}%`, width: `${activeLineWidth}%`}}
                    />
                    <div
                        className={styles.ActiveLineHandle}
                        data-id="ruler-active-line"
                        style={{top: `${lineY}%`, left: `${activeLineMid}%`, width: `${activeLineWidth}%`}}
                        title="Drag up/down"
                        onPointerDown={(e) => {
                            e.preventDefault();
                            (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                            setDraggingActive('active-line');
                        }}
                        onMouseEnter={() => setHoverTarget('active-line')}
                        onMouseLeave={() => setHoverTarget(null)}
                    />

                    <div
                        className={styles.ActiveArrowHandle}
                        data-id="ruler-active-left"
                        style={{left: `${activeLeft}%`, top: `${lineY}%`}}
                        title="Drag to adjust, right-click to switch alignment"
                        onPointerDown={(e) => {
                            if (e.button === 2) {
                                e.preventDefault();
                                setMeasurementSide(toggleRailSide);
                                return;
                            }
                            if (e.button !== 0) return;
                            e.preventDefault();
                            (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                            setDraggingActive('active-left');
                        }}
                        onMouseEnter={() => setHoverTarget('left-arrow')}
                        onMouseLeave={() => setHoverTarget(null)}
                    >
                        <div className={`${styles.ActiveArrowVisual} ${styles.ActiveArrowLeft} ${measurementSide === 'left' ? styles.MarkerYellow : styles.MarkerRed}`} />
                    </div>

                    <div
                        className={styles.ActiveArrowHandle}
                        data-id="ruler-active-right"
                        style={{left: `${activeRight}%`, top: `${lineY}%`}}
                        title="Drag to adjust, right-click to switch alignment"
                        onPointerDown={(e) => {
                            if (e.button === 2) {
                                e.preventDefault();
                                setMeasurementSide(toggleRailSide);
                                return;
                            }
                            if (e.button !== 0) return;
                            e.preventDefault();
                            (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                            setDraggingActive('active-right');
                        }}
                        onMouseEnter={() => setHoverTarget('right-arrow')}
                        onMouseLeave={() => setHoverTarget(null)}
                    >
                        <div className={`${styles.ActiveArrowVisual} ${styles.ActiveArrowRight} ${measurementSide === 'right' ? styles.MarkerYellow : styles.MarkerRed}`} />
                    </div>

                    <div
                        className={`${styles.ValueTag} ${measurementSide === 'left' ? styles.ValueTagSelected : styles.ValueTagSecondary}`}
                        style={{top: `${lineY}%`, left: `${activeLeft}%`}}
                        data-id="ruler-distance-left-label">
                        {formatDistance(__rulerComputed.activeLeftDistanceMeters)}
                    </div>
                    <div
                        className={`${styles.ValueTag} ${measurementSide === 'right' ? styles.ValueTagSelected : styles.ValueTagSecondary}`}
                        style={{top: `${lineY}%`, left: `${activeRight}%`}}
                        data-id="ruler-distance-right-label">
                        {formatDistance(__rulerComputed.activeRightDistanceMeters)}
                    </div>

                    {verticalTopY != null && (
                        <>
                            <div
                                className={styles.VerticalLine}
                                data-id="ruler-vertical-line"
                                style={{
                                    left: `${__rulerComputed.verticalAnchorX}%`,
                                    top: verticalTopY <= lineY
                                        ? `calc(${verticalTopY}% + ${verticalTopCircleRadiusPx}px)`
                                        : `${lineY}%`,
                                    height: `calc(${Math.abs(lineY - verticalTopY)}% - ${verticalTopCircleRadiusPx}px)`
                                }}
                            />
                            <div
                                className={styles.VerticalLineHitArea}
                                style={{
                                    left: `${__rulerComputed.verticalAnchorX}%`,
                                    top: `${Math.min(verticalTopY, lineY)}%`,
                                    height: `${Math.abs(lineY - verticalTopY)}%`,
                                    cursor: verticalLineCursor
                                }}
                                onPointerDown={(e) => {
                                    e.preventDefault();
                                    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                                    const dragMode = getVerticalDragMode(e.clientY, e.currentTarget as HTMLDivElement);
                                    setDraggingActive(dragMode === 'vertical'
                                        ? 'vertical-top'
                                        : measurementSide === 'left' ? 'active-left' : 'active-right');
                                }}
                                onPointerMove={(e) => {
                                    const dragMode = getVerticalDragMode(e.clientY, e.currentTarget as HTMLDivElement);
                                    setVerticalLineCursor(dragMode === 'vertical' ? 'ns-resize' : 'ew-resize');
                                    setHoverTarget(dragMode === 'vertical' ? 'vertical-top' : 'vertical');
                                }}
                                onMouseEnter={() => {
                                    setVerticalLineCursor('ew-resize');
                                    setHoverTarget('vertical');
                                }}
                                onMouseLeave={() => {
                                    setVerticalLineCursor('ew-resize');
                                    setHoverTarget(null);
                                }}
                            />
                            <div
                                className={styles.VerticalTopHandle}
                                data-id="ruler-vertical-top"
                                style={{
                                    left: `${__rulerComputed.verticalAnchorX}%`,
                                    top: `${verticalTopY}%`
                                }}
                                title="Drag to adjust"
                                onPointerDown={(e) => {
                                    e.preventDefault();
                                    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                                    setDraggingActive('vertical-top');
                                }}
                                onMouseEnter={() => setHoverTarget('vertical-top')}
                                onMouseLeave={() => setHoverTarget(null)}
                            >
                                <div className={styles.VerticalTopHandleVisual} />
                            </div>
                            <div
                                className={`${styles.VerticalValueTag} ${styles.ValueTagSecondary}`}
                                data-id="ruler-vertical-label"
                                style={{
                                    left: `${__rulerComputed.verticalAnchorX}%`,
                                    top: `${__rulerComputed.verticalMidY}%`
                                }}
                            >
                                {formatDistance(__rulerComputed.verticalHeightMeters)}
                            </div>

                        </>
                    )}
                </>
            )}

            <div className={styles.Hint} data-id="ruler-hint" style={{top: `calc(${lineY}% - 40px)`, left: `${rulerCenterX}%`}}>
                {getHintText()}
            </div>
        </div>
    );
};

export default RulerOverlay;
